import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function EvolutionMensuelle() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [viewMode, setViewMode] = useState('table');
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'mois', direction: 'asc' });

    useEffect(() => {
        fetchAnnees();
        fetchData();
    }, [annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=annees`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/stats.php?action=evolution_mensuelle&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const mois = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const getMoisDonnees = () => {
        return data.map((item) => ({
            ...item,
            moisLabel: mois[item.mois - 1] || `Mois ${item.mois}`,
            moisNum: item.mois,
            ca: parseFloat(item.ca) || 0,
            nb_factures: parseInt(item.nb_factures) || 0,
            nb_clients: parseInt(item.nb_clients) || 0,
            panier_moyen: parseFloat(item.panier_moyen) || 0
        }));
    };

    const donneesAvecMois = getMoisDonnees();

    const sortedData = React.useMemo(() => {
        const sortableData = [...donneesAvecMois];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                if (sortConfig.key === 'moisLabel') {
                    aVal = a.moisNum;
                    bVal = b.moisNum;
                }
                
                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [donneesAvecMois, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const totalCA = sortedData.reduce((sum, item) => sum + (item.ca || 0), 0);
    const totalFactures = sortedData.reduce((sum, item) => sum + (item.nb_factures || 0), 0);
    const totalClients = sortedData.reduce((sum, item) => sum + (item.nb_clients || 0), 0);
    const caMoyen = totalFactures > 0 ? totalCA / totalFactures : 0;
    const maxCA = Math.max(...sortedData.map(d => d.ca), 1);
    const moisAvecCA = sortedData.filter(d => d.ca > 0).length;

    const moisAbrege = (moisComplet) => {
        return moisComplet.substring(0, 3);
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement de l'évolution mensuelle...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchData}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bar-chart-2-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Évolution mensuelle</h4>
                                    <p className="text-muted small mb-0">
                                        Analyse du chiffre d'affaires • Année {annee}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('table')}
                                className="rounded-pill px-3"
                                style={viewMode === 'table' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-table-line me-1"></i>Tableau
                            </Button>
                            <Button 
                                variant={viewMode === 'graph' ? 'primary' : 'outline-primary'}
                                size="sm"
                                onClick={() => setViewMode('graph')}
                                className="rounded-pill px-3"
                                style={viewMode === 'graph' ? { background: '#21ACCD', border: 'none' } : {}}
                            >
                                <i className="ri-bar-chart-2-line me-1"></i>Graphique
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stats/dashboard')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards modernes */}
                    <Row className="g-3 mb-4">
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-money-euro-circle-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">CA Annuel</span>
                                    <h3 className="kpi-value">{formatCurrency(totalCA)}</h3>
                                    <span className="kpi-unit">total {annee}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon">
                                    <i className="ri-file-list-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Factures</span>
                                    <h3 className="kpi-value">{formatNumber(totalFactures)}</h3>
                                    <span className="kpi-unit">documents</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-group-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Clients</span>
                                    <h3 className="kpi-value">{formatNumber(totalClients)}</h3>
                                    <span className="kpi-unit">acheteurs</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} xl={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-shopping-basket-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Panier moyen</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1rem' }}>{formatCurrency(caMoyen)}</h3>
                                    <span className="kpi-unit">par facture</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres modernes */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-calendar-2-line me-2"></i>
                                            Année
                                        </Form.Label>
                                        <Form.Select 
                                            value={annee} 
                                            onChange={(e) => setAnnee(e.target.value)}
                                            className="rounded-pill"
                                        >
                                            {annees.map(a => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded-pill"
                                        >
                                            <option value="6">6 par page</option>
                                            <option value="12">12 par page</option>
                                            <option value="24">24 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-sort-asc line me-2"></i>
                                            Trier par
                                        </Form.Label>
                                        <Form.Select 
                                            value={sortConfig.key}
                                            onChange={(e) => setSortConfig({ key: e.target.value, direction: 'asc' })}
                                            className="rounded-pill"
                                        >
                                            <option value="moisLabel">Mois</option>
                                            <option value="ca">Chiffre d'affaires</option>
                                            <option value="nb_factures">Factures</option>
                                            <option value="panier_moyen">Panier moyen</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchData}
                                        className="w-100 rounded-pill py-2"
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        <i className="ri-refresh-line me-2"></i>
                                        Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Vue Tableau */}
                    {viewMode === 'table' && (
                        <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-calendar-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">Détail mensuel {annee}</h6>
                                    </div>
                                    <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                        {moisAvecCA} mois actifs sur 12
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4 sortable" onClick={() => handleSort('moisLabel')}>
                                                    Mois 
                                                    {sortConfig.key === 'moisLabel' && (
                                                        <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                    )}
                                                </th>
                                                <th className="text-end sortable" onClick={() => handleSort('ca')}>
                                                    Chiffre d'affaires
                                                    {sortConfig.key === 'ca' && (
                                                        <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                    )}
                                                </th>
                                                <th className="text-end sortable" onClick={() => handleSort('nb_factures')}>
                                                    Factures
                                                    {sortConfig.key === 'nb_factures' && (
                                                        <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                    )}
                                                </th>
                                                <th className="text-end">Clients</th>
                                                <th className="text-end sortable" onClick={() => handleSort('panier_moyen')}>
                                                    Panier moyen
                                                    {sortConfig.key === 'panier_moyen' && (
                                                        <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                                    )}
                                                </th>
                                                <th className="text-center pe-4">Performance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentItems.map((item, index) => {
                                                const moisPrecedent = index > 0 ? sortedData[indexOfFirstItem + index - 1]?.ca : item.ca;
                                                const evolution = moisPrecedent > 0 
                                                    ? ((item.ca - moisPrecedent) / moisPrecedent * 100).toFixed(1) 
                                                    : 0;
                                                
                                                return (
                                                    <tr key={item.mois} className="ca-row">
                                                        <td className="ps-4">
                                                            <span className="fw-semibold text-capitalize">{item.moisLabel}</span>
                                                        </td>
                                                        <td className="text-end fw-bold text-primary">
                                                            {formatCurrency(item.ca)}
                                                        </td>
                                                        <td className="text-end">
                                                            <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#FFF' }}>
                                                                {item.nb_factures}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end">{item.nb_clients || '-'}</td>
                                                        <td className="text-end fw-semibold text-success">
                                                            {formatCurrency(item.panier_moyen)}
                                                        </td>
                                                        <td className="text-center pe-4">
                                                            {index > 0 && item.ca > 0 && (
                                                                <span className={`status-badge ${evolution >= 0 ? 'text-success' : 'text-danger'}`} style={{ background: evolution >= 0 ? '#10b98115' : '#ef444415' }}>
                                                                    <i className={`ri-arrow-${evolution >= 0 ? 'up' : 'down'}-line me-1 fs-10`}></i>
                                                                    {Math.abs(evolution)}%
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                            
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length} mois
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card>
                    )}

                    {/* Vue Graphique */}
                    {viewMode === 'graph' && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="section-icon-small">
                                            <i className="ri-bar-chart-2-line text-primary"></i>
                                        </div>
                                        <h6 className="mb-0 fw-semibold">Évolution du chiffre d'affaires</h6>
                                    </div>
                                    <div className="d-flex gap-3">
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="graph-legend-bar" style={{ background: '#21ACCD' }}></span>
                                            <small>CA mensuel</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="graph-legend-line"></span>
                                            <small>Moyenne: {formatCurrency(caMoyen)}</small>
                                        </div>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {sortedData.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-bar-chart-line fs-48 text-muted mb-3 d-block"></i>
                                        <h5>Aucune donnée à afficher</h5>
                                    </div>
                                ) : (
                                    <div className="graph-container">
                                        <div className="graph-bars">
                                            {sortedData.map((item, index) => {
                                                const hauteur = maxCA > 0 ? (item.ca / maxCA) * 200 : 0;
                                                return (
                                                    <div key={index} className="graph-bar-item">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip>
                                                                    <div className="text-center">
                                                                        <strong>{item.moisLabel}</strong><br />
                                                                        CA: {formatCurrency(item.ca)}<br />
                                                                        Factures: {item.nb_factures}
                                                                    </div>
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <div 
                                                                className="graph-bar"
                                                                style={{ height: `${hauteur}px` }}
                                                            ></div>
                                                        </OverlayTrigger>
                                                        <small className="graph-label">{moisAbrege(item.moisLabel)}</small>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="graph-legend mt-4 pt-3 text-center text-muted small">
                                            <i className="ri-information-line me-1"></i>
                                            Survolez les barres pour voir les détails
                                        </div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Pied de tableau avec totaux */}
                    {viewMode === 'table' && sortedData.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3 mt-3" style={{ background: '#f8fafc' }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2 text-muted small">
                                        <i className="ri-information-line fs-5" style={{ color: '#21ACCD' }}></i>
                                        <span>
                                            <strong>Total {annee}</strong> : {moisAvecCA} mois avec activité
                                        </span>
                                    </div>
                                    <div className="d-flex gap-4">
                                        <span className="fw-bold text-primary">{formatCurrency(totalCA)}</span>
                                        <span className="fw-bold" style={{ color: '#21ACCD' }}>{formatNumber(totalFactures)}</span>
                                        <span className="fw-bold text-success">{formatCurrency(caMoyen)}</span>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-info { border-left-color: #0ea5e9; }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Section Icon Small */
                .section-icon-small {
                    width: 32px;
                    height: 32px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                    white-space: nowrap;
                }
                .sortable {
                    cursor: pointer;
                    user-select: none;
                }
                .sortable:hover {
                    color: #21ACCD;
                    background: #f1f5f9;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .ca-row {
                    transition: all 0.2s ease;
                }
                
                .ca-row:hover {
                    background: #f8fafc;
                }
                
                /* Graphique */
                .graph-container {
                    padding: 1rem;
                }
                .graph-bars {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-around;
                    min-height: 280px;
                }
                .graph-bar-item {
                    text-align: center;
                    width: 60px;
                }
                .graph-bar {
                    background: #21ACCD;
                    width: 36px;
                    margin: 0 auto 0.5rem;
                    border-radius: 8px 8px 0 0;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                .graph-bar:hover {
                    background: #1a8ba8;
                    transform: scaleX(1.05);
                }
                .graph-label {
                    font-size: 0.7rem;
                    color: #64748b;
                }
                .graph-legend-bar {
                    width: 16px;
                    height: 4px;
                    background: #21ACCD;
                    border-radius: 2px;
                }
                .graph-legend-line {
                    width: 30px;
                    height: 2px;
                    background: #10b981;
                    border-radius: 1px;
                }
                .graph-legend {
                    font-size: 0.7rem;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}